import _ from 'underscore';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import { Highcharts } from 'javascripts/analytics/config/highcharts';
import { transformToDayCounts } from 'javascripts/analytics/config/utilities';
import { ChartLegendBox } from 'javascripts/analytics/components/chart_legend_box';
import { SurfacesContext } from 'js/analytics/autotest/AutotestAnalytics';

// Control defaults; the autotest chartSeriesColors / chartGridColor slots
// override these per arm (defaults resolve to exactly these values).
const CHART_COLORS = {
  DOCUMENT_VIEWS: '#188A1f',
  PROFILE_VIEWS: '#0755d3',
  UNIQUE_VISITORS: '#4b4b4b',
};
const DEFAULT_GRID_COLOR = '#E9E9E9';

const getChartConfig = function (startDate, gridLineColor) {
  const pointStart = Date.UTC(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate(),
  ).valueOf();

  return {
    defaultChartConfig: {
      credits: {
        enabled: false,
      },
      xAxis: {
        type: 'datetime',
        tickInterval: 24 * 3600 * 1000,
        labels: {
          enabled: false,
        },
      },
      yAxis: {
        title: false,
        min: 0,
        allowDecimals: false,
        gridLineColor: gridLineColor,
      },
      legend: {
        enabled: false,
      },
      title: {
        text: undefined,
      },
      tooltip: {
        shared: true,
        crosshairs: true,
        dateTimeLabelFormats: {
          hour: '%A, %b %e, %Y',
        },
      },
      time: {
        useUTC: true,
      },
    },
    defaultSeriesConfig: {
      pointStart: pointStart,
      pointInterval: 24 * 3600 * 1000,
      shadow: false,
      marker: {
        symbol: 'circle',
        radius: 4,
        fillColor: '#FFFFFF',
        lineColor: null,
        lineWidth: 2,
      },
    },
  };
};

export class OverviewTabChart extends React.Component {
  static propTypes = {
    hits: PropTypes.array.isRequired,
    startDate: PropTypes.object.isRequired,
    finishDate: PropTypes.object.isRequired,
  };

  uniqueVisitorsByDay = () => {
    const groupedHits = _.groupBy(this.props.hits, function (hit) {
      return moment.unix(hit.epoch / 1000).format('YYYY-MM-DD');
    });

    const groupedUniqueVisitors = {};
    _.each(_.keys(groupedHits), function (dateKey) {
      // _.uniq would be simpler than _.keys(_.groupBy), but it's O(n^2)
      groupedUniqueVisitors[dateKey] = _.keys(
        _.groupBy(groupedHits[dateKey], function (hit) {
          return hit.visitor.id;
        }),
      );
    });

    return transformToDayCounts(groupedUniqueVisitors);
  };

  // Resolve the three series colors from the autotest context (defaults ==
  // CHART_COLORS, so control is unchanged).
  chartColors = () => {
    const cs = (this.context && this.context.chartSeriesColors) || {};
    return {
      DOCUMENT_VIEWS: cs.documentViews || CHART_COLORS.DOCUMENT_VIEWS,
      PROFILE_VIEWS: cs.profileViews || CHART_COLORS.PROFILE_VIEWS,
      UNIQUE_VISITORS: cs.uniqueVisitors || CHART_COLORS.UNIQUE_VISITORS,
    };
  };

  initializeLineGraph = (el) => {
    if (!el) return;

    const colors = this.chartColors();
    const gridColor =
      (this.context && this.context.chartGridColor) || DEFAULT_GRID_COLOR;
    const config = getChartConfig(this.props.startDate, gridColor);
    const groupedWorkHits = _.groupBy(
      _.filter(this.props.hits, function (hit) {
        return hit && hit.page && hit.page.work_id;
      }),
      function (hit) {
        return moment.unix(hit.epoch / 1000).format('YYYY-MM-DD');
      },
    );

    const groupedProfileHits = _.groupBy(
      _.filter(this.props.hits, function (hit) {
        return !(hit && hit.page && hit.page.work_id);
      }),
      function (hit) {
        return moment.unix(hit.epoch / 1000).format('YYYY-MM-DD');
      },
    );

    const series = _.map(
      [
        transformToDayCounts(groupedWorkHits),
        transformToDayCounts(groupedProfileHits),
        this.uniqueVisitorsByDay(),
      ],
      function (singleSeries, index) {
        return _.extend(
          {
            name: ['Document Views', 'Profile Views', 'Unique Visitors'][index],
            data: singleSeries,
            color: [
              colors.DOCUMENT_VIEWS,
              colors.PROFILE_VIEWS,
              colors.UNIQUE_VISITORS,
            ][index],
          },
          config.defaultSeriesConfig,
        );
      },
    );
    const customChartConfig = {
      chart: {
        type: 'line',
        renderTo: el,
      },
      series: series,
    };
    const chartConfig = _.extend(customChartConfig, config.defaultChartConfig);
    new Highcharts.Chart(chartConfig);
  };

  workHitCountFromHits = (hits) => {
    return _.filter(hits, function (hit) {
      return hit.page.work_id;
    }).length;
  };

  nonWorkHitCountFromHits = (hits) => {
    return _.filter(hits, function (hit) {
      return !hit.page.work_id;
    }).length;
  };

  uniqueVisitorCountFromHits = (hits) => {
    return _.values(
      _.groupBy(hits, function (hit) {
        return hit.visitor.id;
      }),
    ).length; // _.uniq would be simpler, but it's O(n^2).
  };

  renderStats = () => {
    const colors = this.chartColors();
    return (
      <div>
        <div className="panel-horizontal-split" />
        <div className="row u-m0x">
          {_.map(
            [
              {
                label: this.props.dayCount + '-day Profile Views',
                color: colors.PROFILE_VIEWS,
                count: this.nonWorkHitCountFromHits(this.props.hits),
                previousDateRangeCount: this.nonWorkHitCountFromHits(
                  this.props.previousDateRangeHits,
                ),
                dayCount: this.props.dayCount,
              },
              {
                label: this.props.dayCount + '-day Document Views',
                color: colors.DOCUMENT_VIEWS,
                count: this.workHitCountFromHits(this.props.hits),
                previousDateRangeCount: this.workHitCountFromHits(
                  this.props.previousDateRangeHits,
                ),
                dayCount: this.props.dayCount,
              },
              {
                label: this.props.dayCount + '-day Unique Visitors',
                color: colors.UNIQUE_VISITORS,
                count: this.uniqueVisitorCountFromHits(this.props.hits),
                previousDateRangeCount: this.uniqueVisitorCountFromHits(
                  this.props.previousDateRangeHits,
                ),
                dayCount: this.props.dayCount,
              },
            ],
            function (props, index) {
              return (
                <div
                  className="col-xs-4 panel-vertical-split u-p0x"
                  key={index}
                >
                  <ChartLegendBox {...props} />
                </div>
              );
            },
          )}
        </div>
      </div>
    );
  };

  render() {
    return (
      <div className="panel panel-flat a-fadeInDown">
        <div className="u-p5x u-tcGrayDarker">Traffic Overview</div>
        <div className="panel-horizontal-split" />
        <div className="panel-body">
          <div
            ref={this.initializeLineGraph}
            key={this.props.hits.length}
          ></div>
        </div>
        {this.renderStats()}
      </div>
    );
  }
}

OverviewTabChart.contextType = SurfacesContext;
